import { Button, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { type $CustomMaterial } from 'metalworks-domain/src/__generated/admin-enhanced-model'
import { createAdminRecipe } from 'metalworks-domain/src/__generated/admin-implementation'
import { CustomMaterial, Finish, RawMaterial } from 'metalworks-domain/src/__generated/admin-model/index.js'
import { useCallback } from 'react'

import {
  DeleteEntityMenuItem,
  EditEntityMenuItem,
  EntityRow,
  ShowEntityMenuItem,
} from '@salsita-ai/platform-project-sdk/admin'
import {
  AttributeSwitchSelector,
  AttributeToggleSelector,
  GenericDimensionInput,
  TextValueInput,
} from '@salsita-ai/platform-project-sdk/react'
import { PhxIcon, PropertyContainer } from '@salsita-ai/platform-project-sdk/ui'

import { adminDomainHooks } from '../systems/admin-hooks'

const recipes = [
  createAdminRecipe({
    type: 'CustomMaterial',
    code: '',
    name: 'New material',
    length: 100,
    width: 100,
    thickness: 1,
    rawMaterial: RawMaterial.brass,
    finish: Finish.brushed,
    visible: true,
    standard: true,
    powderCoatingEnabled: false,
  }),
]

export const MaterialPageActions = () => {
  const { rootEntityId } = adminDomainHooks.useDomain()

  const { disabled, create } = adminDomainHooks.useCookedRecipes(recipes, rootEntityId)

  return (
    <Button
      color="primary"
      variant="contained"
      startIcon={<PhxIcon name="PlusBold" />}
      onClick={create}
      disabled={disabled}
    >
      Add material
    </Button>
  )
}

interface SidebarProps {
  id: $CustomMaterial['id']
}

export const MaterialSidebar = (props: SidebarProps) => {
  const { id } = props

  return (
    <PropertyContainer.Group spacing="medium">
      <PropertyContainer.Item title="Code">
        <TextValueInput entityId={id} instanceName="code" />
      </PropertyContainer.Item>
      <PropertyContainer.Item title="Name">
        <TextValueInput entityId={id} instanceName="name" />
      </PropertyContainer.Item>
      <PropertyContainer.Item title="Length">
        <GenericDimensionInput entityId={id} instanceName="length" />
      </PropertyContainer.Item>
      <PropertyContainer.Item title="Width">
        <GenericDimensionInput entityId={id} instanceName="width" />
      </PropertyContainer.Item>
      <PropertyContainer.Item title="Thickness">
        <GenericDimensionInput entityId={id} instanceName="thickness" />
      </PropertyContainer.Item>
      <PropertyContainer.Item title="Material">
        <AttributeToggleSelector entityId={id} instanceName="rawMaterial" />
      </PropertyContainer.Item>
      <PropertyContainer.Item title="Finish">
        <AttributeToggleSelector entityId={id} instanceName="finish" />
      </PropertyContainer.Item>
      <PropertyContainer.Item title="Visible">
        <AttributeSwitchSelector entityId={id} instanceName="visible" />
      </PropertyContainer.Item>
      <PropertyContainer.Item title="Standard material">
        <AttributeSwitchSelector entityId={id} instanceName="standard" />
      </PropertyContainer.Item>
      <PropertyContainer.Item title="Powder coating enabled">
        <AttributeSwitchSelector entityId={id} instanceName="powderCoatingEnabled" />
      </PropertyContainer.Item>
    </PropertyContainer.Group>
  )
}

interface MaterialActionsProps {
  id: $CustomMaterial['id']
  onClose: () => void
}

const MaterialActions = (props: MaterialActionsProps) => {
  const { id, onClose } = props

  return (
    <>
      <EditEntityMenuItem id={id} onClose={onClose} title="Edit Material" bold />
      <Divider />
      <ShowEntityMenuItem id={id} onClose={onClose} />
      <DeleteEntityMenuItem id={id} onClose={onClose} />
    </>
  )
}

interface MaterialRowProps {
  id: $CustomMaterial['id']
}

const MaterialRow = (props: MaterialRowProps) => {
  const { id } = props

  const state = adminDomainHooks.useEntityState(id)
  const select = adminDomainHooks.useSelectEntity()

  const enter = useCallback(() => {
    select(id)
  }, [select, id])

  return (
    <EntityRow.Root
      key={state.id}
      id={state.id}
      visible={state.visible}
      onClick={enter}
      name={state.code}
      description={`${state.name} (${state.length} x ${state.width} x ${state.thickness} mm, ${state.finish} ${state.rawMaterial})`}
      showThumbnail={false}
      Actions={MaterialActions}
    />
  )
}

export const MaterialsPage = () => {
  const materials = adminDomainHooks.useStoreSelector(e => {
    return e.state.entities.filter(CustomMaterial.is)
  }, [])

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 62 }}>Code</TableCell>
            <TableCell>Name</TableCell>
            <TableCell sx={{ width: '20ch' }}>Status</TableCell>
            <TableCell sx={{ width: 62 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {materials.map(entity => (
            <MaterialRow key={entity.id} id={entity.id} />
          ))}

          {materials.length === 0 && <EntityRow.EmptyRow title="No materials found" span={5} />}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
